import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { setTheme } from './utils/theme';
import api from './services/api';
import config from '../../project_config.json';
import { STRINGS } from './constants/strings';
import { ENDPOINTS } from './constants/endpoints';
import IrisLoader from '@/components/common/IrisLoader';
import VoiceCallOverlay from '@/components/voice/VoiceCallOverlay';
import { useToast } from '@/components/common/ToastProvider';
import SetupWizard from '@/pages/Setup/SetupWizard';
import Login from '@/pages/Login';
import InviteRegister from '@/pages/InviteRegister';
import ResetPassword from '@/pages/ResetPassword';
import Chat from '@/pages/Chat';
import AdminPanel from '@/pages/AdminPanel';
import OpsPanel from '@/pages/Ops';
import { announcementsCache } from '@/services/announcementsCache';
import { favoritesCache } from '@/services/favoritesCache';
import soundConfigService from '@/services/soundConfig';
import soundService from '@/services/sounds';
import { useAutoStatus } from '@/hooks/useAutoStatus';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const token = localStorage.getItem('accessToken');
  if (!token) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function App() {
  const [loading, setLoading] = useState(true);
  const [setupRequired, setSetupRequired] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // --- Dynamic Theme Unification ---
  useEffect(() => {
    const root = document.documentElement;
    const isDark = root.classList.contains('dark');
    const theme = isDark ? config.modes.dark : config.modes.light;

    // Map JSON colors to CSS variables
    Object.entries(theme).forEach(([key, value]) => {
      root.style.setProperty(`--brand-${key}`, value as string);
    });

    // Also inject specific background/text variables for base layer
    root.style.setProperty('--brand-bg', theme.background);
    root.style.setProperty('--brand-text', isDark ? '#e0e7ff' : '#1e1b4b');
  }, []); // Run on mount, though a real theme switcher would trigger this too

  useEffect(() => {
    // Initialize theme
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' | null;
    if (savedTheme) {
      setTheme(savedTheme);
    } else {
      const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setTheme(systemDark ? 'dark' : 'light');
    }

    // Clear all caches on reload except login tokens
    clearAppCaches();
    checkSetupStatus();
    initializeSoundServices();
  }, []);

  const clearAppCaches = () => {
    announcementsCache.clear();
    favoritesCache.clear();
    sessionStorage.clear();
  };

  const initializeSoundServices = async () => {
    try {
      // Initialize sound configuration from server
      await soundConfigService.initialize();
      // Then initialize sound service with user preferences
      await soundService.initialize();
    } catch (error) {
      console.warn('Failed to initialize sound services:', error);
    }
  };

  const checkSetupStatus = async () => {
    try {
      const response = await api.get(ENDPOINTS.setup_status, { timeout: 5000 });
      setSetupRequired(response.data.setupRequired);
      setError(null);
    } catch {
      setError(
        `Failed to connect to server. Make sure the backend is running at ${config.default_server_url}`,
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-background dark:bg-brand-background">
        <div className="text-center">
          <IrisLoader size="xl" message={STRINGS.connecting_message} />
          {error && (
            <div className="text-sm text-brand-error dark:text-brand-error max-w-md mt-6">
              {error}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-background dark:bg-brand-background p-4">
        <div className="text-center p-8 bg-brand-surface dark:bg-brand-surface rounded-lg shadow-xl max-w-lg w-full">
          <div className="mb-6">
            <svg
              className="mx-auto h-16 w-16 text-brand-error dark:text-brand-error"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-brand-on-background dark:text-brand-on-background mb-4">
            Cannot Connect to Backend
          </h1>
          <p className="text-brand-on-surface dark:text-brand-on-surface-variant mb-6 text-lg">
            {error}
          </p>
          <div className="bg-brand-background dark:bg-brand-surface-variant rounded-lg p-4 mb-6 text-left">
            <p className="text-sm font-semibold text-brand-on-background dark:text-brand-on-background mb-2">
              Troubleshooting Steps:
            </p>
            <ul className="text-sm text-brand-on-surface dark:text-brand-on-surface-variant space-y-1 list-disc list-inside">
              <li>Ensure the backend server is running on port 3001</li>
              <li>Check that PostgreSQL and Redis containers are running</li>
              <li>Verify no firewall is blocking port 3001</li>
              <li>Check browser console for detailed error messages</li>
            </ul>
          </div>
          <button
            onClick={() => {
              setError(null);
              setLoading(true);
              checkSetupStatus();
            }}
            className="w-full px-6 py-3 bg-brand-primary text-brand-on-primary rounded-lg hover:bg-brand-primary-dark transition-colors font-semibold shadow-lg hover:shadow-xl"
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <AppRoutes setupRequired={setupRequired} />
    </Router>
  );
}

function AppRoutes({ setupRequired }: { setupRequired: boolean }) {
  const navigate = useNavigate();
  const toast = useToast();

  // Enable automatic presence and status synchronization globally across the app
  useAutoStatus();

  useEffect(() => {
    // Listen for global toast events
    const handleToast = (event: Event) => {
      const customEvent = event as CustomEvent<{
        type: string;
        message: string;
        actionLabel?: string;
        action?: () => void;
        duration?: number;
      }>;
      const { type, message, actionLabel, action, duration } = customEvent.detail;

      switch (type) {
        case 'error':
          toast.error(message, { duration, actionLabel, action });
          break;
        case 'success':
          toast.success(message, { duration, actionLabel, action });
          break;
        case 'info':
          toast.info(message, { duration, actionLabel, action });
          break;
        case 'warning':
          toast.warning(message, { duration, actionLabel, action });
          break;
      }
    };

    window.addEventListener('app:toast', handleToast);
    return () => window.removeEventListener('app:toast', handleToast);
  }, [toast]);

  useEffect(() => {
    // Listen for navigate events from api.ts and other services
    const handleNavigate = (event: Event) => {
      const customEvent = event as CustomEvent<{ to: string; message?: string }>;
      // Show toast if message provided (e.g., session expired)
      if (customEvent.detail.message) {
        // Dispatch toast event since we can't access useToast here
        window.dispatchEvent(
          new CustomEvent('app:toast', {
            detail: { type: 'error', message: customEvent.detail.message },
          }),
        );
      }
      navigate(customEvent.detail.to);
    };

    // Listen for permission denied errors
    const handlePermissionDenied = (event: Event) => {
      const customEvent = event as CustomEvent<{ message: string }>;
      window.dispatchEvent(
        new CustomEvent('app:toast', {
          detail: { type: 'error', message: customEvent.detail.message },
        }),
      );
    };

    // Listen for SW updates and show refresh toast
    const handleSWUpdate = (event: Event) => {
      const customEvent = event as CustomEvent<{ registration: ServiceWorkerRegistration }>;
      const { registration } = customEvent.detail;

      // Save current state before reload
      const currentPath = window.location.pathname + window.location.search;
      sessionStorage.setItem('sw_update_return_path', currentPath);

      window.dispatchEvent(
        new CustomEvent('app:toast', {
          detail: {
            type: 'info',
            message: 'New version available!',
            actionLabel: 'Refresh',
            action: () => {
              registration.waiting?.postMessage({ type: 'SKIP_WAITING' });
              window.location.reload();
            },
            duration: 0, // Don't auto-dismiss
          },
        }),
      );
    };

    window.addEventListener('app:navigate', handleNavigate);
    window.addEventListener('permission-denied', handlePermissionDenied);
    window.addEventListener('sw:update-available', handleSWUpdate);

    return () => {
      window.removeEventListener('app:navigate', handleNavigate);
      window.removeEventListener('permission-denied', handlePermissionDenied);
      window.removeEventListener('sw:update-available', handleSWUpdate);
    };
  }, [navigate]);

  return (
    <>
      <main className="flex-1 w-full h-full flex flex-col min-h-0">
        <Routes>
          {setupRequired ? (
            <>
              <Route path="/setup" element={<SetupWizard />} />
              <Route path="*" element={<Navigate to="/setup" replace />} />
            </>
          ) : (
            <>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<InviteRegister />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route
                path="/chat"
                element={
                  <ProtectedRoute>
                    <Chat />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <Chat initialView="admin" />
                  </ProtectedRoute>
                }
              />
              <Route path="/ops" element={<OpsPanel />} />
              <Route path="/" element={<Navigate to="/chat" replace />} />
              <Route path="*" element={<Navigate to="/chat" replace />} />
            </>
          )}
        </Routes>
      </main>
      {/* Global voice call overlay - always visible when in call */}
      <VoiceCallOverlay />
    </>
  );
}

export default App;
